import { isEqual } from 'lodash/fp';
import { toast } from 'react-toastify';
import { all, call, debounce, fork, put, select, takeEvery, takeLeading, takeLatest } from 'redux-saga/effects';

import { fetchAllDocumentGroups, fetchAllDocumentNames } from '../../../../services/document';
import { fetchAllEntitiesForAccessGroups } from '../../../../services/entity';
import { deleteAccessGroup, fetchAllAccessGroups, upsertAccessGroup } from '../../../../services/accessGroup';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { ClientDocumentGroup, DocumentNameDB } from '../../documents/store/types';
import { clearAccessGroupFilters, deleteAccessGroupFailed, deleteAccessGroupStarted, deleteAccessGroupSuccessful, fetchAllAccessGroupsFailed, fetchAllAccessGroupsSuccessful, fetchAllEntitiesForAccessGroupsFailed, fetchAllEntitiesForAccessGroupsStarted, fetchAllEntitiesForAccessGroupsSuccessful, fetchDocumentGroupsForAccessGroupsFailed, fetchDocumentGroupsForAccessGroupsStarted, fetchDocumentGroupsForAccessGroupsSuccessful, fetchDocumentNamesForAccessGroupsFailed, fetchDocumentNamesForAccessGroupsStarted, fetchDocumentNamesForAccessGroupsSuccessful, saveAccessGroupFailed, saveAccessGroupSuccessful, setAccessGroupFilters, setAccessGroupUpdated, setGroupNameIsUnique, toggleAccessGroupColumnSort } from './actions';
import { getAccessGroupColumnSort, getAccessGroupFilters, getAllAccessGroups, getCurrentAccessGroup, getSavedAccessGroup } from './selectors';
import { AccessGroup, AccessGroupEntity, AccessGroupTableName, AccessGroupActionTypes } from './types';
import { fetchAllUsersStarted } from '../../users/store/actions';

export function* attemptFetchAllAccessGroups() {
    try {
        const accessGroups: AccessGroup[] = yield call(fetchAllAccessGroups);
        yield put(fetchAllAccessGroupsSuccessful(accessGroups));
    } catch (e) {
        toast.error('Unable to fetch the latest list of access groups. Please try again.');
        yield put(fetchAllAccessGroupsFailed((e as Error).message));
    }
}

function* fetchAllAccessGroupsWatcher() {
    yield takeLeading(AccessGroupActionTypes.FETCH_ALL_ACCESS_GROUPS_STARTED, attemptFetchAllAccessGroups);
}

export function* attemptFetchAllEntitiesForAccessGroups() {
    try {
        const filters: TableFilters = yield select(getAccessGroupFilters(AccessGroupTableName.ENTITY));
        const columnSort: ColumnSort | undefined = yield select(getAccessGroupColumnSort(AccessGroupTableName.ENTITY));
        const accessGroupEntities: AccessGroupEntity[] = yield call(fetchAllEntitiesForAccessGroups, { filters, columnSort });
        yield put(fetchAllEntitiesForAccessGroupsSuccessful(accessGroupEntities));
    } catch (e) {
        toast.error('Unable to fetch the latest list of access groups. Please try again.');
        yield put(fetchAllEntitiesForAccessGroupsFailed((e as Error).message));
    }
}

function* fetchAllEntitiesForAccessGroupsWatcher() {
    yield takeLatest(AccessGroupActionTypes.FETCH_ALL_ENTITIES_FOR_ACCESS_GROUPS_STARTED, attemptFetchAllEntitiesForAccessGroups);
}

export function* attemptFetchAllDocumentNamesForAccessGroups() {
    try {
        const filters: TableFilters = yield select(getAccessGroupFilters(AccessGroupTableName.DOCUMENT));
        const columnSort: ColumnSort | undefined = yield select(getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT));
        const documentNames: DocumentNameDB[] = yield call(fetchAllDocumentNames, { filters, columnSort, activeOnly: true });
        yield put(fetchDocumentNamesForAccessGroupsSuccessful(documentNames));
    } catch (e) {
        yield put(fetchDocumentNamesForAccessGroupsFailed((e as Error).message));
        toast.error('Unable to fetch the document names. Please try again.');
    }
}

function* fetchAllDocumentNamesForAccessGroupsWatcher() {
    yield takeLatest(AccessGroupActionTypes.FETCH_DOCUMENT_NAMES_FOR_ACCESS_GROUPS_STARTED, attemptFetchAllDocumentNamesForAccessGroups);
}

export function* attemptFetchAllDocumentGroupsForAccessGroups() {
    try {
        const filters: TableFilters = yield select(getAccessGroupFilters(AccessGroupTableName.DOCUMENT_GROUP));
        const columnSort: ColumnSort | undefined = yield select(getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT_GROUP));
        const documentGroups: ClientDocumentGroup[] = yield call(fetchAllDocumentGroups, { filters, columnSort });
        yield put(fetchDocumentGroupsForAccessGroupsSuccessful(documentGroups));
    } catch (e) {
        yield put(fetchDocumentGroupsForAccessGroupsFailed((e as Error).message));
        toast.error('Unable to fetch the list of document groups. Please try again.');
    }
}

function* fetchAllDocumentGroupsForAccessGroupsWatcher() {
    yield takeLatest(AccessGroupActionTypes.FETCH_DOCUMENT_GROUPS_FOR_ACCESS_GROUPS_STARTED, attemptFetchAllDocumentGroupsForAccessGroups);
}

export function* accessGroupTableColumnSortUpdated({ payload }: ReturnType<typeof toggleAccessGroupColumnSort>) {
    const { columnSort: { field }, table } = payload;
    if (!['entityIncluded', 'userIncluded', 'documentIncluded', 'groupIncluded', 'roleIncluded'].includes(field)) {
        if (table === AccessGroupTableName.ENTITY) {
            yield put(fetchAllEntitiesForAccessGroupsStarted());
        }
        if (table === AccessGroupTableName.USER) {
            yield put(fetchAllUsersStarted());
        }
        if (table === AccessGroupTableName.DOCUMENT) {
            yield put(fetchDocumentNamesForAccessGroupsStarted());
        }
        if (table === AccessGroupTableName.DOCUMENT_GROUP) {
            yield put(fetchDocumentGroupsForAccessGroupsStarted());
        }
    }
}

function* accessGroupTableColumnSortUpdatedWatcher() {
    yield debounce(200, AccessGroupActionTypes.TOGGLE_ACCESS_GROUP_COLUMN_SORT, accessGroupTableColumnSortUpdated);
}

export function* accessGroupTableFiltersUpdated({ payload }: ReturnType<typeof setAccessGroupFilters>) {
    if (payload.table === AccessGroupTableName.ENTITY) {
        yield put(fetchAllEntitiesForAccessGroupsStarted());
    }
    if (payload.table === AccessGroupTableName.USER) {
        yield put(fetchAllUsersStarted());
    }
    if (payload.table === AccessGroupTableName.DOCUMENT) {
        yield put(fetchDocumentNamesForAccessGroupsStarted());
    }
    if (payload.table === AccessGroupTableName.DOCUMENT_GROUP) {
        yield put(fetchDocumentGroupsForAccessGroupsStarted());
    }
}

function* accessGroupTableFiltersUpdatedWatcher() {
    yield debounce(200, AccessGroupActionTypes.SET_ACCESS_GROUP_FILTERS, accessGroupTableFiltersUpdated);
}

export function* accessGroupTableFiltersCleared({ payload }: ReturnType<typeof clearAccessGroupFilters>) {
    if (payload.table === AccessGroupTableName.ENTITY) {
        yield put(fetchAllEntitiesForAccessGroupsStarted());
    }
    if (payload.table === AccessGroupTableName.USER) {
        yield put(fetchAllUsersStarted());
    }
    if (payload.table === AccessGroupTableName.DOCUMENT) {
        yield put(fetchDocumentNamesForAccessGroupsStarted());
    }
    if (payload.table === AccessGroupTableName.DOCUMENT_GROUP) {
        yield put(fetchDocumentGroupsForAccessGroupsStarted());
    }
}

function* accessGroupTableFiltersClearedWatcher() {
    yield takeEvery(AccessGroupActionTypes.CLEAR_ACCESS_GROUP_FILTERS, accessGroupTableFiltersCleared);
}

export function* attemptCheckIfGroupNameIsUnique() {
    try {
        const currentAccessGroup: AccessGroup = yield select(getCurrentAccessGroup);
        const allAccessGroups: AccessGroup[] = yield select(getAllAccessGroups);
        const currentAccessGroupNames = (currentAccessGroup.accessGroupId ? allAccessGroups.filter(({ accessGroupId }) => accessGroupId !== currentAccessGroup.accessGroupId) : allAccessGroups).map(({ accessGroupName }) => accessGroupName);
        const isValid = !currentAccessGroupNames.includes(currentAccessGroup.accessGroupName);
        yield put(setGroupNameIsUnique(isValid));
    } catch (e) {
        toast.error('Unable to check if the group name is valid.');
        yield put(fetchAllEntitiesForAccessGroupsFailed((e as Error).message));
    }
}

function* checkIfGroupNameIsUniqueWatcher() {
    yield debounce(150, [AccessGroupActionTypes.UPDATE_ACCESS_GROUP, AccessGroupActionTypes.TOGGLE_ACCESS_GROUP_MODAL], attemptCheckIfGroupNameIsUnique);
}

export function* attemptCheckIfGroupUpdated() {
    try {
        const currentAccessGroup: AccessGroup = yield select(getCurrentAccessGroup);
        const savedAccessGroup: AccessGroup = yield select(getSavedAccessGroup);

        const nameUpdated = currentAccessGroup.accessGroupName !== savedAccessGroup.accessGroupName;
        const descriptionUpdated = currentAccessGroup.description !== savedAccessGroup.description;
        const userIdsUpdated = !isEqual(currentAccessGroup.userIds, savedAccessGroup.userIds);
        const entityIdsUpdated = !isEqual(currentAccessGroup.entityIds, savedAccessGroup.entityIds);
        const documentNameIdsUpdated = !isEqual(currentAccessGroup.documentNameIds, savedAccessGroup.documentNameIds);
        const roleIdsUpdated = !isEqual(currentAccessGroup.roleIds, savedAccessGroup.roleIds);

        const hasUpdated = nameUpdated || descriptionUpdated || userIdsUpdated || entityIdsUpdated || documentNameIdsUpdated || roleIdsUpdated;
        yield put(setAccessGroupUpdated(hasUpdated));

    } catch (e) {
        toast.error('Unable to check if the group has updated.');
        yield put(fetchAllEntitiesForAccessGroupsFailed((e as Error).message));
    }
}

function* checkIfGroupUpdatedWatcher() {
    yield debounce(150, [AccessGroupActionTypes.UPDATE_ACCESS_GROUP, AccessGroupActionTypes.TOGGLE_ACCESS_GROUP_MODAL], attemptCheckIfGroupUpdated);
}

export function* attemptSaveAccessGroup() {
    try {
        const currentAccessGroup: AccessGroup = yield select(getCurrentAccessGroup);
        const accessGroups: AccessGroup[] = yield call(upsertAccessGroup, currentAccessGroup);
        yield put(saveAccessGroupSuccessful(accessGroups));
    } catch (e) {
        toast.error('Unable to save the access group. Please try again.');
        yield put(saveAccessGroupFailed((e as Error).message));
    }
}

function* saveAccessGroupWatcher() {
    yield takeLeading(AccessGroupActionTypes.SAVE_ACCESS_GROUP_STARTED, attemptSaveAccessGroup);
}

export function* attemptDeleteAccessGroup({ payload }: ReturnType<typeof deleteAccessGroupStarted>) {
    try {
        const { accessGroupId } = payload;
        const allAccessGroups: AccessGroup[] = yield call(deleteAccessGroup, { accessGroupId });
        yield put(deleteAccessGroupSuccessful(allAccessGroups));
        toast('Access group successfully deleted.');
    } catch (e) {
        toast.error('Unable to delete access group. Please try again.');
        yield put(deleteAccessGroupFailed());
    }
}

function* deleteAccessGroupWatcher() {
    yield takeLeading(AccessGroupActionTypes.DELETE_ACCESS_GROUP_STARTED, attemptDeleteAccessGroup);
}

export function* accessGroupSaga() {
    yield all([
        fork(fetchAllAccessGroupsWatcher),
        fork(fetchAllEntitiesForAccessGroupsWatcher),
        fork(checkIfGroupNameIsUniqueWatcher),
        fork(checkIfGroupUpdatedWatcher),
        fork(saveAccessGroupWatcher),
        fork(accessGroupTableFiltersUpdatedWatcher),
        fork(fetchAllDocumentNamesForAccessGroupsWatcher),
        fork(fetchAllDocumentGroupsForAccessGroupsWatcher),
        fork(accessGroupTableColumnSortUpdatedWatcher),
        fork(accessGroupTableFiltersClearedWatcher),
        fork(deleteAccessGroupWatcher)
    ]);
}
